import { memo, useRef, useState } from 'react';

import { Trash2Icon, UploadIcon } from 'lucide-react';
import { twMerge } from 'tailwind-merge';

import { TableCell, TableRow, Tooltip } from '@/_components';
import { ActionButton } from '@/_containers/actions';
import { CellText } from '@/_containers/data-table';
import type { FileQuerySchema } from '@/_schemas/file';
import { formatFileSize } from '@/_shared';

import { FileDownloadButton } from './file-download-button';
import { FilePreview } from './file-preview';

interface FileRowProps {
  file: FileQuerySchema;
  onRemove: (fileId: number) => void;
  disabled: boolean;
  onUpdate: (fileId: number, file: File) => Promise<void>;
  allowDelete: boolean;
  isLastItem: boolean;
}

function FileRowComponent({ file, isLastItem, disabled, onRemove, onUpdate, allowDelete }: FileRowProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUpdating, setIsUpdating] = useState(false);

  // const handleOpenFile = () => {
  //   if (file.url) {
  //     window.open(file.url, '_blank', 'noopener,noreferrer');
  //   }
  // };

  const handleEditClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      try {
        const selectedFile = event.target.files[0];

        setIsUpdating(true);
        await onUpdate(file.id, selectedFile);

        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      } catch (error) {
        console.error('File update failed:', error);
      } finally {
        setIsUpdating(false);
      }
    }
  };

  return (
    <TableRow>
      <TableCell className={twMerge(isLastItem && 'rounded-bl-2xl')}>
        <Tooltip content={file.name}>
          <div className="flex items-center gap-2">
            <FilePreview filename={file?.name ?? ''} fileUrl={file.url} mimeType={file?.mime_type ?? ''} />
            <CellText className="overflow-hidden break-all text-ellipsis">{file.name}</CellText>
          </div>
        </Tooltip>
      </TableCell>
      <TableCell>
        <CellText className="whitespace-nowrap" align="right">
          {formatFileSize(file.file_size || 0)}
        </CellText>
      </TableCell>
      <TableCell className={twMerge(isLastItem && 'rounded-br-2xl')}>
        <div className="flex items-center justify-end gap-1.5">
          <FileDownloadButton fileUrl={file.url} fileName={file?.name ?? ''} />
          {!disabled && (
            <>
              <input type="file" ref={fileInputRef} className="hidden" onChange={handleFileChange} accept="*/*" />
              <ActionButton
                size="sm"
                title="Replace File"
                tag="button"
                icon={UploadIcon}
                type="icon"
                onClick={handleEditClick}
                showLoader={isUpdating}
              />
              {allowDelete && (
                <ActionButton
                  size="sm"
                  title="Delete File"
                  tag="button"
                  icon={Trash2Icon}
                  type="icon"
                  onClick={() => onRemove(file.id)}
                />
              )}
            </>
          )}
        </div>
      </TableCell>
    </TableRow>
  );
}

export const FileRow = memo(FileRowComponent);
